/**
 * Pricing debug data extraction for admin PDF and internal tooling.
 *
 * IMPORTANT: profitMargin is stored as a PERCENTAGE (e.g., 40 means 40%)
 * Formula: billable = rawCost / (1 - margin/100)
 * Example: $10,000 raw with 40% margin = $10,000 / 0.60 = $16,667 billable
 */

import { ProposalNodeSet, DesignNodeData, ProductionNodeData, FreightNodeData, OverheadNodeData, InstallNodeData, TravelNodeData, MaterialsNodeData, DesignEstimatingData } from '../types';
import { ProposalRateSettings, DEFAULT_PROPOSAL_RATES } from './appSettings';
import { getEffectiveProjectOverheadPercent } from './proposalCalculations';
import { estimatingRaw } from './proposalDesignBreakdown';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// Minimal interface for proposal data (works with ProposalState and ProposalBuilderData)
export interface DebugProposalInput {
    id?: string;
    nodeSets: ProposalNodeSet[];
    profitMargin: number;  // PERCENTAGE, e.g., 40 = 40%
    installOptional?: boolean;
    isKitProposal?: boolean;
    offeredSpecies?: string[];
    activeSpecies?: string;
    kitPricing?: Array<{
        speciesName: string;
        totalCost: number;
        structurePricing?: Array<{ nodeSetId: string; nodeSetName: string; cost: number }>
    }>;
    // Extended fields for enhanced debug (optional — callers pass what they have)
    displayTotal?: number;         // Stored displayTotal from Firestore
    updatedAt?: Date | string;     // Last save timestamp
    sentAt?: Date | string;        // When proposal was sent
    createdAt?: Date | string;     // When proposal was created
    pricingLocked?: boolean;       // Whether pricing changes are frozen
    sourceKitId?: string;          // Source kit ID (if kit proposal)
    sourceKitName?: string;        // Source kit name
    status?: string;               // Proposal status
    slug?: string;                 // Public URL slug
    billableTotal?: number;        // Stored billableTotal from Firestore
    rawCostTotal?: number;         // Stored rawCostTotal
    kitMarginPercent?: number;     // Margin used when kit was built (from kit doc)
    globalCosts?: import('../types').GlobalCosts;  // Global project-level costs
    designEstimating?: DesignEstimatingData | null;
}

export interface SpeciesPricingBreakdown {
    speciesName: string;
    isDefault: boolean;
    timberTotal: number;
    materialsTotal: number;  // timber + pegs + post bases + fasteners
    timberKitRaw: number;    // production + materials + freight
    structureRawTotal: number;
    structureRawTotalWithQty: number;
}

export interface DebugStructureData {
    id: string;
    name: string;
    quantity: number;
    clientOptional?: boolean;

    // Raw costs per category (for default species)
    designRaw: number;
    productionRaw: number;
    materialsRaw: number;
    freightRaw: number;
    overheadRaw: number;
    installRaw: number;
    travelRaw: number;

    // Totals (for default species)
    structureRawTotal: number;
    structureRawTotalWithQty: number;

    // Details for materials
    timberTotal: number;
    totalBoardFeet: number;
    defaultSpeciesName: string;

    // Per-species full breakdown (default + alternates)
    speciesPricing: SpeciesPricingBreakdown[];

    // Raw node data (all input fields)
    designNode: DesignNodeData;
    productionNode: ProductionNodeData;
    freightNode: FreightNodeData;
    overheadNode: OverheadNodeData;
    installNode: InstallNodeData;
    travelNode: TravelNodeData;
    materialsNode: MaterialsNodeData;
}


// Rounding audit: shows pre-round and post-round values for each bucket
export interface RoundingAuditEntry {
    structureName: string;
    structureId: string;
    qty: number;
    // design
    designPreRound: number;
    designPostRound: number;
    designRoundingLoss: number;
    // timberKit
    timberKitPreRound: number;
    timberKitPostRound: number;
    timberKitRoundingLoss: number;
    // install (separate)
    installPreRound: number;
    installPostRound: number;
    installRoundingLoss: number;
    // travel (separate)
    travelPreRound: number;
    travelPostRound: number;
    travelRoundingLoss: number;
    // install+travel combined (how PDF/public-page fallback does it)
    installCombinedPreRound: number;
    installCombinedPostRound: number;
    installCombinedRoundingLoss: number;
    // total rounding diff: separate vs combined
    roundingDiffSeparateVsCombined: number;
}

export interface DebugBucketTotals {
    // Raw costs
    designRaw: number;
    timberKitRaw: number;  // production + materials + freight + overhead
    installRaw: number;    // install + travel
    globalCostsRaw: number; // global project-level costs
    /** From global rate setting: % of (structure + global raw). */
    projectOverheadRaw: number;
    grandTotalRaw: number;

    // Billable (with profit margin applied)
    designBillable: number;
    timberKitBillable: number;
    installBillable: number;
    globalCostsBillable: number;
    grandTotalBillable: number;

    // Profit
    grossProfit: number;
}

export interface PricingDebugData {
    // Proposal metadata
    proposalId: string;
    proposalType: 'custom' | 'kit';
    profitMarginPercent: number;  // e.g., 40 for 40%
    profitMarginDecimal: number;  // e.g., 0.40
    marginMultiplier: number;     // e.g., 1.667 (1 / 0.60)
    installOptional: boolean;
    structureCount: number;

    // Structure breakdowns
    structures: DebugStructureData[];

    // Bucket totals
    buckets: DebugBucketTotals;

    // Kit pricing (if kit proposal)
    kitPricing?: Array<{ speciesName: string; totalCost: number; structurePricing?: Array<{ nodeSetId: string; nodeSetName: string; cost: number }> }>;
    offeredSpecies?: string[];
    activeSpecies?: string;

    // Stored billable prices (what's actually saved on the proposal - for sanity check)
    storedBillablePrices: Array<{
        structureId: string;
        structureName: string;
        design: number;
        timberKit: number;
        install: number;
        travel: number;
        subtotal: number;
        speciesOptions?: Record<string, number>;  // { "Douglas Fir": 25000, "White Oak": 27500 }
        selectedSpecies?: string;  // Which species is currently selected for this structure
    }>;

    // Timestamp
    generatedAt: string;

    // Rates used for calculations
    rates: ProposalRateSettings;

    // ============ ENHANCED DEBUG FIELDS ============

    // Proposal health / staleness
    updatedAt?: string;            // ISO timestamp of last save
    sentAt?: string;               // ISO timestamp when proposal was sent
    createdAt?: string;            // ISO timestamp of creation
    proposalStatus?: string;       // current status (draft, sent, approved, etc.)
    pricingLocked?: boolean;       // whether pricing is frozen

    // Display total comparison
    displayTotal?: number;         // Stored displayTotal (what deal cards show)
    recalculatedGrandTotal: number; // What we just calculated
    storedBillableTotal?: number;   // Stored billableTotal from Firestore
    storedRawCostTotal?: number;    // Stored rawCostTotal from Firestore

    // Kit-specific
    sourceKitId?: string;
    sourceKitName?: string;
    kitMarginPercent?: number;     // Margin used in KitBuilder (may differ from proposal margin)
    kitMarginMatchesProposal?: boolean;  // Whether they match

    // Global project costs
    globalCosts?: import('../types').GlobalCosts;
    designEstimating?: DesignEstimatingData | null;

    // Rounding audit
    roundingAudit: RoundingAuditEntry[];
    totalRoundingImpact: number;  // Sum of all rounding diff (separate vs combined)

    // Firestore snapshot
    firestoreSnapshot: {
        slug?: string;
        status?: string;
        pricingLocked?: boolean;
        installOptional?: boolean;
        isKitProposal?: boolean;
        sourceKitId?: string;
        sourceKitName?: string;
        profitMargin?: number;
        displayTotal?: number;
        billableTotal?: number;
        rawCostTotal?: number;
        structureCount?: number;
        offeredSpecies?: string[];
        activeSpecies?: string;
    };
}

// ============================================================================
// CALCULATION FUNCTIONS
// ============================================================================

/**
 * Apply profit margin to raw cost
 * Uses TRUE margin formula: finalPrice = cost / (1 - margin/100)
 * 
 * @param rawCost - The raw cost before margin
 * @param marginPercent - Margin as PERCENTAGE (e.g., 40 for 40%)
 * @returns Billable price with margin applied
 */
export const applyMargin = (rawCost: number, marginPercent: number): number => {
    if (marginPercent >= 100) {
        return rawCost * 10; // Cap at 10x for safety
    }
    return rawCost / (1 - marginPercent / 100);
};

/**
 * Extract debug data from a single structure
 */
const extractStructureData = (ns: ProposalNodeSet): DebugStructureData => {
    const quantity = ns.quantity || 1;

    const designRaw = ns.design.total;
    const productionRaw = ns.production.total;
    const materialsRaw = ns.materials.total;
    const freightRaw = ns.freight.total;
    const overheadRaw = ns.overhead.total;
    const installRaw = ns.install.total;
    const travelRaw = ns.travel.total;

    const structureRawTotal = designRaw + productionRaw + materialsRaw +
        freightRaw + overheadRaw + installRaw + travelRaw;

    // Calculate non-timber materials cost (pegs, post bases, fasteners)
    const nonTimberMaterials = materialsRaw - ns.materials.timberTotal;

    // Get default species name from timber list
    const defaultSpeciesName = ns.materials.timberList?.[0]?.species || 'Default';

    // Build species pricing array
    const speciesPricing: SpeciesPricingBreakdown[] = [];

    // 1. Default species
    // Timber Kit = Production + Materials + Freight + Overhead
    const defaultTimberKitRaw = productionRaw + materialsRaw + freightRaw + overheadRaw;
    speciesPricing.push({
        speciesName: defaultSpeciesName,
        isDefault: true,
        timberTotal: ns.materials.timberTotal,
        materialsTotal: materialsRaw,
        timberKitRaw: defaultTimberKitRaw,
        structureRawTotal,
        structureRawTotalWithQty: structureRawTotal * quantity,
    });

    // 2. Alternate species
    (ns.materials.alternateSpecies || []).forEach(altSpecies => {
        const altMaterialsTotal = altSpecies.timberTotal + nonTimberMaterials;
        // Timber Kit = Production + altMaterials + Freight + Overhead
        const altTimberKitRaw = productionRaw + altMaterialsTotal + freightRaw + overheadRaw;
        const altStructureRawTotal = designRaw + altTimberKitRaw + installRaw + travelRaw;

        speciesPricing.push({
            speciesName: altSpecies.speciesName,
            isDefault: false,
            timberTotal: altSpecies.timberTotal,
            materialsTotal: altMaterialsTotal,
            timberKitRaw: altTimberKitRaw,
            structureRawTotal: altStructureRawTotal,
            structureRawTotalWithQty: altStructureRawTotal * quantity,
        });
    });

    return {
        id: ns.id,
        name: ns.name,
        quantity,
        clientOptional: ns.clientOptional === true,
        designRaw,
        productionRaw,
        materialsRaw,
        freightRaw,
        overheadRaw,
        installRaw,
        travelRaw,
        structureRawTotal,
        structureRawTotalWithQty: structureRawTotal * quantity,
        timberTotal: ns.materials.timberTotal,
        totalBoardFeet: ns.materials.totalBoardFeet,
        defaultSpeciesName,
        speciesPricing,
        // Raw node data (all input fields)
        designNode: ns.design,
        productionNode: ns.production,
        freightNode: ns.freight,
        overheadNode: ns.overhead,
        installNode: ns.install,
        travelNode: ns.travel,
        materialsNode: ns.materials,
    };
};

/**
 * Generate comprehensive pricing debug data from a proposal
 */
export const generatePricingDebugData = (proposal: DebugProposalInput, rates?: ProposalRateSettings): PricingDebugData => {
    const marginPercent = proposal.profitMargin;
    const marginDecimal = marginPercent / 100;
    const marginMultiplier = 1 / (1 - marginDecimal);

    // Extract structure data
    const structures = proposal.nodeSets.map(ns => extractStructureData(ns));

    // Calculate bucket totals (matching proposalCalculations.ts logic)
    let designRaw = 0;
    let timberKitRaw = 0; // production + materials + freight + overhead
    let installRaw = 0;   // install + travel

    proposal.nodeSets.forEach(ns => {
        const qty = ns.quantity || 1;

        // Design
        designRaw += ns.design.total * qty;

        // Timber Kit (Production + Materials + Freight + Overhead)
        timberKitRaw += ns.production.total * qty;
        timberKitRaw += ns.materials.total * qty;
        timberKitRaw += ns.freight.total * qty;
        timberKitRaw += ns.overhead.total * qty;

        // Install (Install + Travel) — install node already includes overhead automatically
        installRaw += ns.install.total * qty;
        installRaw += ns.travel.total * qty;
    });

    const globalCostsRaw =
        (proposal.globalCosts?.totalGlobalCosts || 0) +
        estimatingRaw(proposal.designEstimating, rates, proposal.status);
    const baseBeforeProjectOverhead = designRaw + timberKitRaw + installRaw + globalCostsRaw;
    const poPct = getEffectiveProjectOverheadPercent(proposal.globalCosts);
    const projectOverheadRaw =
        poPct > 0 ? Math.round(baseBeforeProjectOverhead * (poPct / 100)) : 0;
    const grandTotalRaw = baseBeforeProjectOverhead + projectOverheadRaw;

    // Apply margin to get billable
    const designBillable = Math.round(applyMargin(designRaw, marginPercent));
    const timberKitBillable = Math.round(applyMargin(timberKitRaw, marginPercent));
    const installBillable = Math.round(applyMargin(installRaw, marginPercent));
    const globalCostsBillable = Math.round(applyMargin(globalCostsRaw, marginPercent));
    const grandTotalBillable = Math.round(applyMargin(grandTotalRaw, marginPercent));

    const grossProfit = grandTotalBillable - grandTotalRaw;

    const buckets: DebugBucketTotals = {
        designRaw,
        timberKitRaw,
        installRaw,
        globalCostsRaw,
        projectOverheadRaw,
        grandTotalRaw,
        designBillable,
        timberKitBillable,
        installBillable,
        globalCostsBillable,
        grandTotalBillable,
        grossProfit,
    };

    // Kit pricing (include structurePricing for multi-structure kits)
    let kitPricing: Array<{ speciesName: string; totalCost: number; structurePricing?: Array<{ nodeSetId: string; nodeSetName: string; cost: number }> }> | undefined;
    if (proposal.isKitProposal && proposal.kitPricing) {
        kitPricing = proposal.kitPricing.map(kp => ({
            speciesName: kp.speciesName,
            totalCost: kp.totalCost,
            structurePricing: kp.structurePricing,
        }));
    }

    // Extract stored billable prices (for sanity check)
    const storedBillablePrices = proposal.nodeSets
        .filter(ns => ns.billablePrices)
        .map(ns => ({
            structureId: ns.id,
            structureName: ns.name,
            design: ns.billablePrices!.design,
            timberKit: ns.billablePrices!.timberKit,
            install: ns.billablePrices!.install,
            travel: ns.billablePrices!.travel,
            subtotal: ns.billablePrices!.subtotal,
            speciesOptions: ns.billablePrices!.speciesOptions,
        }));

    // ============ ROUNDING AUDIT ============
    const roundingAudit: RoundingAuditEntry[] = proposal.nodeSets.map(ns => {
        const qty = ns.quantity || 1;
        const designPre = applyMargin(ns.design.total, marginPercent) * qty;
        const designPost = Math.round(designPre);
        const tkRaw = ns.production.total + ns.materials.total + ns.freight.total + ns.overhead.total;
        const tkPre = applyMargin(tkRaw, marginPercent) * qty;
        const tkPost = Math.round(tkPre);
        const instPre = applyMargin(ns.install.total + (ns.install.overheadTotal || 0), marginPercent) * qty;
        const instPost = Math.round(instPre);
        const travPre = applyMargin(ns.travel.total, marginPercent) * qty;
        const travPost = Math.round(travPre);
        // Combined install+travel (what PDF/public-page fallback does)
        const combinedPre = applyMargin(ns.install.total + ns.travel.total + (ns.install.overheadTotal || 0), marginPercent) * qty;
        const combinedPost = Math.round(combinedPre);
        const separateTotal = instPost + travPost;
        return {
            structureName: ns.name,
            structureId: ns.id,
            qty,
            designPreRound: designPre,
            designPostRound: designPost,
            designRoundingLoss: designPost - designPre,
            timberKitPreRound: tkPre,
            timberKitPostRound: tkPost,
            timberKitRoundingLoss: tkPost - tkPre,
            installPreRound: instPre,
            installPostRound: instPost,
            installRoundingLoss: instPost - instPre,
            travelPreRound: travPre,
            travelPostRound: travPost,
            travelRoundingLoss: travPost - travPre,
            installCombinedPreRound: combinedPre,
            installCombinedPostRound: combinedPost,
            installCombinedRoundingLoss: combinedPost - combinedPre,
            roundingDiffSeparateVsCombined: separateTotal - combinedPost,
        };
    });
    const totalRoundingImpact = roundingAudit.reduce((sum, r) => sum + r.roundingDiffSeparateVsCombined, 0);

    // ============ TIMESTAMP HELPERS ============
    const toISO = (val?: Date | string | { toDate?: () => Date }): string | undefined => {
        if (!val) return undefined;
        if (typeof val === 'string') return val;
        if (val instanceof Date) return val.toISOString();
        if (typeof (val as any).toDate === 'function') return (val as any).toDate().toISOString();
        return undefined;
    };

    // ============ ASSEMBLE ============
    const debugData: PricingDebugData = {
        proposalId: proposal.id || 'UNSAVED',
        proposalType: proposal.isKitProposal ? 'kit' : 'custom',
        profitMarginPercent: marginPercent,
        profitMarginDecimal: marginDecimal,
        marginMultiplier,
        installOptional: proposal.installOptional || false,
        structureCount: proposal.nodeSets.length,
        structures,
        buckets,
        kitPricing,
        offeredSpecies: proposal.offeredSpecies,
        activeSpecies: proposal.activeSpecies,
        storedBillablePrices,
        generatedAt: new Date().toISOString(),
        rates: rates || DEFAULT_PROPOSAL_RATES,

        // Enhanced debug fields
        updatedAt: toISO(proposal.updatedAt),
        sentAt: toISO(proposal.sentAt),
        createdAt: toISO(proposal.createdAt),
        proposalStatus: proposal.status,
        pricingLocked: proposal.pricingLocked,

        displayTotal: proposal.displayTotal,
        recalculatedGrandTotal: grandTotalBillable,
        storedBillableTotal: proposal.billableTotal,
        storedRawCostTotal: proposal.rawCostTotal,

        sourceKitId: proposal.sourceKitId,
        sourceKitName: proposal.sourceKitName,
        kitMarginPercent: proposal.kitMarginPercent,
        kitMarginMatchesProposal: proposal.kitMarginPercent != null
            ? proposal.kitMarginPercent === marginPercent
            : undefined,

        roundingAudit,
        totalRoundingImpact,

        firestoreSnapshot: {
            slug: proposal.slug,
            status: proposal.status,
            pricingLocked: proposal.pricingLocked,
            installOptional: proposal.installOptional,
            isKitProposal: proposal.isKitProposal,
            sourceKitId: proposal.sourceKitId,
            sourceKitName: proposal.sourceKitName,
            profitMargin: proposal.profitMargin,
            displayTotal: proposal.displayTotal,
            billableTotal: proposal.billableTotal,
            rawCostTotal: proposal.rawCostTotal,
            structureCount: proposal.nodeSets.length,
            offeredSpecies: proposal.offeredSpecies,
            activeSpecies: proposal.activeSpecies,
        },

        // Global project costs
        globalCosts: proposal.globalCosts,
        designEstimating: proposal.designEstimating,
    };

    return debugData;
};
